// pages/Home.jsx
import React from 'react';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/home/HeroSection';
import ServicesSection from '../components/home/ServicesSection';
import TeamSection from '../components/about/TeamSection';
import { Microscope, Users, SmilePlus } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <Layout>
      <HeroSection />

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Why Choose Zia Dental?
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-gray-500 mx-auto">
              We combine expertise with compassion to deliver the best dental care.
            </p>
          </motion.div>

          <div className="mt-14">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: <Microscope className="h-6 w-6" />,
                  title: "Modern Technology",
                  description: "Latest dental technology for precise treatment",
                  gradient: "from-blue-500 to-cyan-400"
                },
                {
                  icon: <Users className="h-6 w-6" />,
                  title: "Experienced Team",
                  description: "Compassionate and qualified professionals with years of experience in the field.",
                  gradient: "from-blue-600 to-indigo-500"
                },
                {
                  icon: <SmilePlus className="h-6 w-6" />,
                  title: "Patient Comfort",
                  description: "Comfortable environment and dental care for a stress-free experience.",
                  gradient: "from-blue-500 to-sky-400"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                >
                  <div className={`flex items-center justify-center h-14 w-14 rounded-xl bg-gradient-to-br ${feature.gradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mt-5">{feature.title}</h3>
                  <p className="mt-2 text-gray-500 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ServicesSection />
      <TeamSection />

      {/* CTA Section */}
      <section className="relative overflow-hidden">
        <div className="hero-gradient">
          <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-20 lg:px-8 lg:flex lg:items-center lg:justify-between">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                <span className="block">Ready to get started?</span>
                <span className="block text-blue-200 mt-1">Book your appointment today.</span>
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-8 flex lg:mt-0 lg:flex-shrink-0"
            >
              <div className="inline-flex rounded-lg shadow-xl">
                <Link
                  to="/appointment"
                  className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-bold rounded-lg text-blue-600 bg-white hover:bg-blue-50 transition-all duration-300 hover:shadow-2xl hover:scale-105"
                >
                  Book Now
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;